import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Tooltip } from "./tooltips.js";
import { handleUIEvent } from "./eventLogger.js";
import CompareTagSearch from "./_114_compare_tag_search.js";
                    
import "./styles.css";
const MODULE_NAME = "_6_max_intraday_compare";

// Utility to format date from "YYYY-MM-DD" to "MM-DD-YY"
function formatDateDisplay(dateStr) {
    if (!dateStr || typeof dateStr !== "string" || dateStr.length < 10) return dateStr;
    const [year, month, day] = dateStr.split("-");
    return `${month}-${day}-${year.slice(2)}`;
}

// Color helper for values (no color, just formatting)
function colorValue(val, decimals = 2) {
    if (val === null || val === undefined || isNaN(val)) return <span>—</span>;
    return <span>{Number(val).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}</span>;
}

// Color helper for integer values (for # trades)
function colorInt(val) {
    if (val === null || val === undefined || isNaN(val)) return <span>—</span>;
    return <span>{parseInt(val, 10).toLocaleString()}</span>;
}

// Color helper for % change (only % change columns get color)
function colorPercent(val) {
    if (val === "" || val === null || isNaN(val)) return "";
    if (val > 0) return <span style={{ color: "#228B22", fontWeight: 500 }}>+{(val * 100).toFixed(1)}%</span>;
    if (val < 0) return <span style={{ color: "#B22222", fontWeight: 500 }}>{(val * 100).toFixed(1)}%</span>;
    return <span>{(val * 100).toFixed(1)}%</span>;
}

// Add a color helper for max qty
function colorQty(val) {
    if (val === null || val === undefined || isNaN(val)) return <span>—</span>;
    return <span style={{ fontWeight: 500 }}>{parseInt(val, 10).toLocaleString()}</span>;
}

// --- Gear Icon Component ---
const GearIcon = ({ size = 24, color = "#fff" }) => (
    <span
        style={{
            fontSize: size,
            color,
            verticalAlign: "middle",
            cursor: "pointer",
            userSelect: "none",
            lineHeight: 1,
            display: "inline-block",
        }}
        title="Settings"
        aria-label="Settings"
    >
        &#9881;
    </span>
);

// --- Toggle Switch Component ---
function ToggleSwitch({ checked, onChange, label, description }) {
    return (
        <div style={{ marginBottom: 16 }}>
            <label style={{
                display: "inline-flex",
                alignItems: "center",
                cursor: "pointer",
                width: "100%"
            }}>
                <span style={{ marginRight: 12, fontWeight: 600, minWidth: 200 }}>{label}</span>
                <span style={{
                    position: "relative",
                    display: "inline-block",
                    width: 44,
                    height: 24,
                    marginRight: 12
                }}>
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => onChange(e.target.checked)}
                        style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                        position: "absolute",
                        cursor: "pointer",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: checked ? "#1976d2" : "#888",
                        borderRadius: 24,
                        transition: "background 0.2s"
                    }} />
                    <span style={{
                        position: "absolute",
                        left: checked ? 22 : 2,
                        top: 2,
                        width: 20,
                        height: 20,
                        background: "#fff",
                        borderRadius: "50%",
                        transition: "left 0.2s"
                    }} />
                </span>
            </label>
            {description && (
                <div style={{ color: "#aaa", fontSize: 11, marginTop: 4, marginLeft: 212 }}>
                    {description}
                </div>
            )}
        </div>
    );
}

const DATE2_COLOR = "#1976d2";
const DATE2_BG = "rgba(25, 118, 210, 0.10)";

// Range options for date selection
const RANGE_OPTIONS = [
    { label: "Last 2", value: "Last 2" },
    { label: "Last 5", value: "Last 5" },
    { label: "Last 7", value: "Last 7" },
    { label: "Last 15", value: "Last 15" },
    { label: "Last 30", value: "Last 30" }
];

// New options for relative date ranges
const LAST_WEEK_OPTION = { label: "Last week", value: "last_week" };
const THIS_WEEK_OPTION = { label: "This week", value: "this_week" };

// Add special options for largest loss/win in last 90 days
const LARGEST_LOSS_90_OPTION = { label: "Largest loss - 90", value: "__largest_loss_90__" };
const LARGEST_WIN_90_OPTION = { label: "Largest win - 90", value: "__largest_win_90__" };

// Helper for % change calculation (always Date2/Date1 - 1)
function percentChange(newVal, oldVal) {
    const n = Number(newVal);
    const o = Number(oldVal);
    if (isNaN(n) || isNaN(o) || o === 0) return "";
    return ((n / o) - 1);
}

// Helper to determine view type
function getViewType(date1) {
    const rangeOptions = ["Last 2", "Last 5", "Last 7", "Last 15", "Last 30", "last_week", "this_week"];
    if (rangeOptions.includes(date1)) {
        if (date1 === "last_week" || date1 === "this_week") return "weekly";
        if (date1 === "Last 30") return "monthly";
        return "weekly";
    }
    return "daily";
}

export default function MaxQtyCompare({ userId, activeDate, screen }) {
    const [resolvedDate1, setResolvedDate1] = useState(null);
    const [resolvedDate2, setResolvedDate2] = useState(null);

    const [availableDates, setAvailableDates] = useState([]);
    const [date1, setDate1] = useState("");
    const [date2, setDate2] = useState("");
    const [tableData, setTableData] = useState(null);
    const [tooltip, setTooltip] = useState("");
    const [error, setError] = useState("");
    const [userChangedDates, setUserChangedDates] = useState(false);
    const [useDollarAmt, setUseDollarAmt] = useState(false);
    const [date1DollarAmt, setDate1DollarAmt] = useState(null);
    const [date2DollarAmt, setDate2DollarAmt] = useState(null);
    const [useSymbolProfit, setUseSymbolProfit] = useState(true);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showTagSearch, setShowTagSearch] = useState(false);
    const [selectedTag1, setSelectedTag1] = useState(null);
    const [selectedTag2, setSelectedTag2] = useState(null);
    const [tagMetrics1, setTagMetrics1] = useState(null);
    const [tagMetrics2, setTagMetrics2] = useState(null);
    const [tagLoading1, setTagLoading1] = useState(false);
    const [tagLoading2, setTagLoading2] = useState(false);

    const skipOrderingRef = useRef(false);

    useEffect(() => {
        function syncCalendarDates() {
            const calDate1 = sessionStorage.getItem("calendar_date_1") || "";
            const calDate2 = sessionStorage.getItem("calendar_date_2") || "";
            if (calDate1 && !availableDates.includes(calDate1)) {
                setAvailableDates(prev => [...prev, calDate1]);
            }
            if (calDate2 && !availableDates.includes(calDate2)) {
                setAvailableDates(prev => [...prev, calDate2]);
            }
            setDate1(calDate1);
            setDate2(calDate2);
            setUserChangedDates(false);
        }
        window.addEventListener("calendarDatesChanged", syncCalendarDates);
        return () => window.removeEventListener("calendarDatesChanged", syncCalendarDates);
    }, [availableDates]);

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/api/maxqty-compare-dates`, {
            withCredentials: true
        })
            .then(res => {
                const dates = Array.from(new Set((res.data.dates || []).sort((a, b) => b.localeCompare(a))));
                setAvailableDates(dates);
                setTooltip(res.data.tooltip || "");
                if (dates.length > 1) {
                    setDate2(dates[0]);
                    setDate1(dates[1]);
                } else if (dates.length === 1) {
                    setDate2(dates[0]);
                    setDate1(dates[0]);
                }
                setUserChangedDates(false);
            })
            .catch(() => setError("Failed to load available dates."));
    }, []);

    // Utility: parse "MM-DD-YY" to Date
    function parseCompactDate(str) {
        if (!str || typeof str !== "string") return null;
        if (str.startsWith("__largest")) return null;
        if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return new Date(str + "T00:00:00");
        if (/^\d{2}-\d{2}-\d{2}$/.test(str)) {
            const [mm, dd, yy] = str.split("-");
            const fullYear = Number(yy) + 2000;
            return new Date(`${fullYear}-${mm}-${dd}T00:00:00`);
        }
        return null;
    }

    function isValidOrdering(d1, d2) {
        const rangeOptions = ["Last 2", "Last 5", "Last 7", "Last 15", "Last 30", "last_week", "this_week"];
        if (!d1 || !d2) return true;
        if (d1.startsWith("__largest") || d2.startsWith("__largest")) return true;
        if (rangeOptions.includes(d1) || rangeOptions.includes(d2)) return true;
        const dt1 = parseCompactDate(d1);
        const dt2 = parseCompactDate(d2);
        if (!dt1 || !dt2) return true;
        return dt1.getTime() <= dt2.getTime();
    }

    useEffect(() => {
        if (!date1 || !date2) return;
        setError("");
        if (!isValidOrdering(date1, date2)) {
            if (skipOrderingRef.current) {
                skipOrderingRef.current = false;
            } else {
                setError("Please choose a second date that comes after the first.");
                setTableData(null);
                return;
            }
        }

        const resolveDateParam = (val) => val;

        axios.get(`${process.env.REACT_APP_API_URL}/api/maxqty-compare`, {
            params: {
                date1: resolveDateParam(date1),
                date2: resolveDateParam(date2)
            },
            withCredentials: true
        })
            .then(res => {
                setTableData(res.data.table);
                setTooltip(res.data.tooltip || "");
                setDate1DollarAmt(res.data.date1_max_intraday_dollar_amt ?? null);
                setDate2DollarAmt(res.data.date2_max_intraday_dollar_amt ?? null);

                if (date1.startsWith("__largest_") && res.data.table?.date1_daily_dates?.length > 0) {
                    setResolvedDate1(res.data.table.date1_daily_dates[0]);
                } else {
                    setResolvedDate1(null);
                }
                if (date2.startsWith("__largest_") && res.data.table?.date2_daily_dates?.length > 0) {
                    setResolvedDate2(res.data.table.date2_daily_dates[0]);
                } else {
                    setResolvedDate2(null);
                }
            })
            .catch(() => setError("Failed to load comparison data."));
    }, [date1, date2]);

    function buildOptions(...groups) {
        const seen = new Set();
        const result = [];
        for (const group of groups) {
            for (const opt of group) {
                if (!seen.has(opt.value)) {
                    seen.add(opt.value);
                    result.push(opt);
                }
            }
        }
        return result;
    }

    const date1Options = buildOptions( RANGE_OPTIONS, availableDates.map(d => ({ label: formatDateDisplay(d), value: d })) );
    const date2Options = buildOptions( RANGE_OPTIONS, availableDates.map(d => ({ label: formatDateDisplay(d), value: d })) );

    function getDateHeaderLabel(options, selectedValue, resolvedDate) {
        let displayDate;
        if (selectedValue && selectedValue.startsWith("__largest_") && resolvedDate) {
            displayDate = formatDateDisplay(resolvedDate);
        } else {
            const opt = options.find(o => o.value === selectedValue);
            displayDate = opt ? opt.label : selectedValue;
        }
        
        // Parse "MM-DD-YY" format and convert to two-line display
        if (/^\d{2}-\d{2}-\d{2}$/.test(displayDate)) {
            const [mm, dd, yy] = displayDate.split("-");
            const fullYear = Number(yy) + 2000;
            return (
                <div style={{ whiteSpace: "pre-line", lineHeight: 1.3 }}>
                    {fullYear}
                    <br />
                    {mm}/{dd}
                </div>
            );
        }
        
        return displayDate;
    }

    const viewType = getViewType(date1);

    function handleDate1Change(e) { setDate1(e.target.value); setUserChangedDates(true); handleUIEvent(e, { name: "date1-select", value: e.target.value }, null, screen, MODULE_NAME); }
    function handleDate2Change(e) { setDate2(e.target.value); setUserChangedDates(true); handleUIEvent(e, { name: "date2-select", value: e.target.value }, null, screen, MODULE_NAME); }
    function handleProfitModeChange(checked) {
        setUseSymbolProfit(checked);
        handleUIEvent({ type: "change" }, { name: "profitModeToggle", mode: checked ? "symbol_profit" : "daily_profit" }, null, screen, MODULE_NAME);
    }

    const handleTagSelect1 = async (tag) => {
        setSelectedTag1(tag);
        setTagLoading1(true);
        setError("");
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/maxqty-compare-tag`, {
                params: { tag: tag.tag_name },
                withCredentials: true
            });
            if (response.data && response.data.metrics) {
                setTagMetrics1(response.data.metrics);
            }
        } catch (err) { setError(`Failed to load Tag 1: ${err.message}`); } finally { setTagLoading1(false); }
    };

    const handleTagSelect2 = async (tag) => {
        setSelectedTag2(tag);
        setTagLoading2(true);
        setError("");
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/maxqty-compare-tag`, {
                params: { tag: tag.tag_name },
                withCredentials: true
            });
            if (response.data && response.data.metrics) {
                setTagMetrics2(response.data.metrics);
            }
        } catch (err) { setError(`Failed to load Tag 2: ${err.message}`); } finally { setTagLoading2(false); }
    };

    const renderSettingsModal = () => {
        if (!showSettingsModal) return null;
        return (
            <>
                <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(24,26,37,0.35)", backdropFilter: "blur(2px)", zIndex: 9998 }} onClick={() => setShowSettingsModal(false)} />
                <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 9999, minWidth: 500, minHeight: 300, background: "#181A25", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,255,255,0.08)", padding: 24 }}>
                    <h3 style={{ marginTop: 0, marginBottom: 20, color: "#fff" }}>Comparison Settings</h3>
                    <ToggleSwitch checked={useSymbolProfit} onChange={handleProfitModeChange} label="Symbol Profit" description="Checked = Symbol profit (default). Uncheck = Daily profit." />
                    <button onClick={() => setShowSettingsModal(false)} style={{ position: "absolute", top: 12, right: 18, background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer" }}>✕</button>
                </div>
            </>
        );
    };

    // Unified helper to render table rows
    const renderDataRow = (label, val1, val2, renderFn, showPct = true) => {
        const pct = showPct ? percentChange(val2, val1) : "";
        return (
            <tr className="maxqty-compare-row">
                <td style={{ padding: "8px 4px" }}>{label}</td>
                <td style={{ padding: "8px 4px" }}>{renderFn(val1)}</td>
                <td style={{ padding: "8px 4px", color: DATE2_COLOR, background: DATE2_BG }}>{renderFn(val2)}</td>
                <td style={{ padding: "8px 4px", textAlign: "center" }}>{colorPercent(pct)}</td>
            </tr>
        );
    };

    return (
        <div data-analytics={MODULE_NAME}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>📊 Largest Intra-day Compare <Tooltip text={tooltip} /></h3>
                <span onClick={() => setShowSettingsModal(true)} style={{ cursor: "pointer" }}><GearIcon size={24} color="#fff" /></span>
            </div>

            {!showTagSearch ? (
                <>
                    <div style={{ display: "flex", gap: 24, marginBottom: 12, marginTop: 8 }}>
                        <div>
                            <label style={{ fontWeight: "bold", marginRight: 8 }}>Date 1:</label>
                            <select value={date1} onChange={handleDate1Change} className="dropdown-curved">
                                {date1Options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ fontWeight: "bold", marginRight: 8, color: DATE2_COLOR }}>Date 2:</label>
                            <select value={date2} onChange={handleDate2Change} className="dropdown-curved" style={{ background: DATE2_BG, color: DATE2_COLOR, border: `2px solid ${DATE2_COLOR}`, fontWeight: 600 }}>
                                {date2Options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>
                        </div>          
                    </div>
                    <div style={{ marginBottom: 24 }}>
                        <button 
                            onClick={() => { 
                                setShowTagSearch(true); 
                                // REMOVED: setTableData(null); -> Keep date data so it's there when we come back
                            }} 
                            className="dropdown-curved" 
                            style={{ padding: "6px 16px", background: "#1976d2", color: "#fff", border: "none", cursor: "pointer" }}
                        >
                            🏷️ Tag Search
                        </button>
                    </div>
                </>
            ) : (
                <div style={{ marginBottom: 24, marginTop: 8 }}>
                    <div style={{ display: "flex", gap: 24, marginBottom: 16 }}>
                        <div style={{ flex: 1 }}>
                            <h4 style={{ margin: "0 0 8px 0", color: "#fff" }}>Tag 1:</h4>
                            <CompareTagSearch onTagSelect={handleTagSelect1} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <h4 style={{ margin: "0 0 8px 0", color: DATE2_COLOR }}>Tag 2:</h4>
                            <CompareTagSearch onTagSelect={handleTagSelect2} />
                        </div>
                    </div>
                    <button onClick={() => { setShowTagSearch(false); setTagMetrics1(null); setTagMetrics2(null); }} className="dropdown-curved" style={{ padding: "6px 16px", background: "#666", color: "#fff", border: "none", cursor: "pointer" }}>Back to Dates</button>
                </div>
            )}

            {error && <div style={{ color: "red", marginBottom: 16 }}>{error}</div>}
            {renderSettingsModal()}

            {/* Revised condition: Show table if we have data for the active mode */}
            {((!showTagSearch && tableData) || (showTagSearch && (tagMetrics1 || tagMetrics2))) && !error && (
                <div style={{ marginTop: 24 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", background: "#222", color: "#fff", borderRadius: 8 }}>
                        <thead>
                            <tr style={{ background: "#333" }}>
                                <th style={{ padding: "8px 4px", textAlign: "left" }}>Item</th>
                                <th style={{ padding: "8px 4px", textAlign: "left" }}
                                    >{showTagSearch ? (selectedTag1?.tag_name || "Tag 1") : getDateHeaderLabel(date1Options, date1, resolvedDate1)}</th>
                                <th style={{ padding: "8px 4px", textAlign: "left", color: DATE2_COLOR, background: DATE2_BG }}
                                    >{showTagSearch ? (selectedTag2?.tag_name || "Tag 2") : getDateHeaderLabel(date2Options, date2, resolvedDate2)}</th>
                                <th style={{ padding: "8px 4px", textAlign: "center" }}>% Change</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!showTagSearch ? (
                                // Date Comparison Rows (Using tableData)
                                <>
                                    {renderDataRow("Max Qty Bucket", tableData?.max_qty_bucket_label1, tableData?.max_qty_bucket_label2, (v) => v, false)}
                                    {renderDataRow("Largest intra day Qty (Avg)", tableData?.max_qty1, tableData?.max_qty2, colorQty)}
                                    {renderDataRow("Largest Day Max Qty", tableData?.largest_day_max_qty1, tableData?.largest_day_max_qty2, colorQty)}
                                    {renderDataRow("# Trades", tableData?.trades1, tableData?.trades2, colorInt)}
                                    {renderDataRow("# Symbols", tableData?.num_symbols1, tableData?.num_symbols2, colorInt)}
                                    {renderDataRow("Risk %", tableData?.risk1, tableData?.risk2, colorValue)}
                                    {renderDataRow("P/L", tableData?.pl1, tableData?.pl2, colorValue)}
                                    {renderDataRow("Price @ Max Qty", tableData?.price_at_max_qty1, tableData?.price_at_max_qty2, colorValue)}
                                    {renderDataRow("Max Intra-day Dollar Amount (All)", date1DollarAmt, date2DollarAmt, colorValue)}
                                </>
                            ) : (
                                // Tag Comparison Rows (Using tagMetrics)
                                <>
                                    {renderDataRow("Max Qty Bucket", tagMetrics1?.bucket, tagMetrics2?.bucket, (v) => v || "—", false)}
                                    {renderDataRow("Largest intra day Qty (Avg)", tagMetrics1?.max_qty, tagMetrics2?.max_qty, colorQty)}
                                    {renderDataRow("# Trades", tagMetrics1?.trades, tagMetrics2?.trades, colorInt)}
                                    {renderDataRow("# Symbols", tagMetrics1?.num_symbols, tagMetrics2?.num_symbols, colorInt)}
                                    {renderDataRow("Price @ Max Qty", tagMetrics1?.price_at_max_qty, tagMetrics2?.price_at_max_qty, colorValue)}
                                    {renderDataRow("Max Intra-day Dollar Amount", tagMetrics1?.max_intraday_dollar_amt, tagMetrics2?.max_intraday_dollar_amt, colorValue)}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
            {(tagLoading1 || tagLoading2) && <div style={{ textAlign: "center", color: "#aaa", marginTop: 12 }}>Loading analytics...</div>}
        </div>
    );
}

// Logic helpers
function getExtremePLInfo(plList, dateList) { return { loss: null, lossDate: null, gain: null, gainDate: null }; }
function ensureDateInDropdown(date, availableDates) { return availableDates; }

